import moment from "moment-timezone"
import { FreightDiscriminator, Shipment, ShipmentConfirmation } from "@/model/transportorder/transportOrder"
import { getTimezoneOfLoading } from "@/timezones/timezones"
import {
    useLazyGetTransportOrdersByNumberQuery,
    usePostFreightForwarderResponseMutation,
} from "@/client/transportorder-hub/transportOrderHubApi"
import { useAssetsOfAccount } from "@/hooks/assets"
import {
    createLicensePlateAssetLookup,
    resolveLicensePlateAsset,
} from "@/helper/licensePlateAssetResolver"
import { Asset as AccountAsset } from "@/model/asset/asset"
import { IntlShape, useIntl } from "react-intl"
import {
    CONFIRMATION_UPLOAD_CSV_ERROR_MESSAGE_IDS,
    ConfirmationUploadType,
    ParsedConfirmationRow,
    parseConfirmationCsv,
    validateConfirmationRow,
} from "./confirmationUploadCsv"

const CONFIRMATION_UPLOAD_PROCESSING_ERROR_MESSAGE_IDS = {
    transportOrderLookupFailed: "transportOrder.confirmationUpload.error.transportOrderLookupFailed",
    transportOrderNotFound: "transportOrder.confirmationUpload.error.transportOrderNotFound",
    transportOrderShipFromDunsNotFound:
        "transportOrder.confirmationUpload.error.transportOrderShipFromDunsNotFound",
    transportOrderAmbiguous: "transportOrder.confirmationUpload.error.transportOrderAmbiguous",
    loadingTimezoneNotFound: "transportOrder.confirmationUpload.error.loadingTimezoneNotFound",
    confirmationFailed: "transportOrder.confirmationUpload.error.confirmationFailed",
} as const

export const CONFIRMATION_UPLOAD_ERROR_MESSAGE_IDS = {
    ...CONFIRMATION_UPLOAD_CSV_ERROR_MESSAGE_IDS,
    ...CONFIRMATION_UPLOAD_PROCESSING_ERROR_MESSAGE_IDS,
} as const

type ConfirmationUploadErrorMessageId =
    typeof CONFIRMATION_UPLOAD_ERROR_MESSAGE_IDS[keyof typeof CONFIRMATION_UPLOAD_ERROR_MESSAGE_IDS]

export interface UploadResult {
    sourceLine: number
    transportOrderNumber: string
    status: "SUCCESS" | "ERROR"
    errorReason?: string
}

export interface ProcessedUpload {
    results: UploadResult[]
}

export interface ConfirmationUploadProcessor {
    process(
        file: File,
        onRowProcessed?: (result: UploadResult) => void
    ): Promise<ProcessedUpload>
}

const errorReasonFor = (
    row: ParsedConfirmationRow,
    uploadType: ConfirmationUploadType,
    intl: IntlShape
): string | undefined => {
    const messageId = validateConfirmationRow(row, uploadType)
    return messageId === undefined ? undefined : intl.formatMessage({ id: messageId })
}

const errorResult = (row: ParsedConfirmationRow, errorReason: string): UploadResult => ({
    sourceLine: row.sourceLine,
    transportOrderNumber: row.transportOrderNumber,
    status: "ERROR",
    errorReason,
})

const successResult = (row: ParsedConfirmationRow): UploadResult => ({
    sourceLine: row.sourceLine,
    transportOrderNumber: row.transportOrderNumber,
    status: "SUCCESS",
})

class ConfirmationRowError extends Error {
    public constructor(public readonly messageId: ConfirmationUploadErrorMessageId) {
        super(messageId)
    }
}


type LoadTransportOrders = (transportOrderNumber: string) => Promise<Shipment[]>

const loadTransportOrder = async (
    row: ParsedConfirmationRow,
    loadTransportOrders: LoadTransportOrders
): Promise<Shipment> => {
    let matchingShipments: Shipment[]
    try {
        matchingShipments = await loadTransportOrders(row.transportOrderNumber)
    } catch {
        throw new ConfirmationRowError(CONFIRMATION_UPLOAD_ERROR_MESSAGE_IDS.transportOrderLookupFailed)
    }

    if (matchingShipments.length === 0) {
        throw new ConfirmationRowError(CONFIRMATION_UPLOAD_ERROR_MESSAGE_IDS.transportOrderNotFound)
    }

    if (row.shipFromDuns !== "") {
        matchingShipments = matchingShipments.filter(shipment => {
            const orderShipFromDuns =
                shipment.currentTransportLeg.placeOfDeparture.externalIdentifiers.duns ??
                (shipment.isStartOfTransportChain
                    ? shipment.startOfTransportChain.consignor.externalIdentifiers.duns
                    : undefined)
            return orderShipFromDuns === row.shipFromDuns
        })
        if (matchingShipments.length === 0) {
            throw new ConfirmationRowError(
                CONFIRMATION_UPLOAD_ERROR_MESSAGE_IDS.transportOrderShipFromDunsNotFound
            )
        }
    }

    if (matchingShipments.length !== 1) {
        throw new ConfirmationRowError(CONFIRMATION_UPLOAD_ERROR_MESSAGE_IDS.transportOrderAmbiguous)
    }

    return matchingShipments[0]
}

const resolveLoadingTimezone = (shipment: Shipment): string => {
    const timezone = getTimezoneOfLoading(shipment)
    if (timezone === undefined) {
        throw new ConfirmationRowError(CONFIRMATION_UPLOAD_ERROR_MESSAGE_IDS.loadingTimezoneNotFound)
    }

    return timezone
}

const toShipmentConfirmation = (
    row: ParsedConfirmationRow,
    shipment: Shipment,
    timezone: string,
    matchedAsset?: AccountAsset
): ShipmentConfirmation => {
    const accessCodeField = shipment.freight.type === "CARGO_MATERIAL"
        ? "accessCodeAtUnloading"
        : "accessCodeAtLoading"
    const accessCode = row.loadingAccessCode || shipment.confirmationDraft[accessCodeField]

    return {
        shipmentId: shipment.id,
        transportOrderId: shipment.transport_order_id,
        version: shipment.version,
        confirmation: {
            asset: {
                ...(matchedAsset ? { id: matchedAsset.id } : {}),
                licensePlate: row.licensePlate,
            },
            loadingTimeWindow: {
                time_from: moment.tz(row.pickupTimeFrom, "YYYYMMDDHHmm", true, timezone).toISOString(true),
                time_to: moment.tz(row.pickupTimeTo, "YYYYMMDDHHmm", true, timezone).toISOString(true),
            },
            ...(row.transportChainReference === ""
                ? {}
                : { transportChainReference: row.transportChainReference }),
            ...(accessCode === undefined
                ? {}
                : { [accessCodeField]: accessCode }),
        },
    }
}

type SubmitConfirmation = (confirmation: ShipmentConfirmation) => Promise<void>

const buildAndSubmitConfirmation = async (
    row: ParsedConfirmationRow,
    shipment: Shipment,
    timezone: string,
    matchedAsset: AccountAsset | undefined,
    submitConfirmation: SubmitConfirmation
): Promise<void> => {
    const confirmation = toShipmentConfirmation(row, shipment, timezone, matchedAsset)

    try {
        await submitConfirmation(confirmation)
    } catch {
        throw new ConfirmationRowError(CONFIRMATION_UPLOAD_ERROR_MESSAGE_IDS.confirmationFailed)
    }
}

const reportResult = (
    results: UploadResult[],
    result: UploadResult,
    onRowProcessed?: (result: UploadResult) => void
): void => {
    results.push(result)
    onRowProcessed?.(result)
}

const processConfirmationRows = async (
    rows: readonly ParsedConfirmationRow[],
    assets: readonly AccountAsset[],
    loadTransportOrders: LoadTransportOrders,
    submitConfirmation: SubmitConfirmation,
    uploadType: ConfirmationUploadType,
    intl: IntlShape,
    onRowProcessed?: (result: UploadResult) => void
): Promise<UploadResult[]> => {
    const results: UploadResult[] = []
    const assetLookup = createLicensePlateAssetLookup(assets)

    for (const row of rows) {
        const rowErrorReason = errorReasonFor(row, uploadType, intl)
        if (rowErrorReason !== undefined) {
            reportResult(results, errorResult(row, rowErrorReason), onRowProcessed)
            continue
        }

        try {
            const parsedRow = row
            const shipment = await loadTransportOrder(parsedRow, loadTransportOrders)
            const timezone = resolveLoadingTimezone(shipment)
            const matchedAsset = resolveLicensePlateAsset(assetLookup, parsedRow.licensePlate)
            await buildAndSubmitConfirmation(parsedRow, shipment, timezone, matchedAsset, submitConfirmation)
            reportResult(results, successResult(parsedRow), onRowProcessed)
        } catch (error) {
            if (!(error instanceof ConfirmationRowError)) {
                throw error
            }
            reportResult(results, errorResult(row, intl.formatMessage({ id: error.messageId })), onRowProcessed)
        }
    }

    return results
}

const FREIGHT_TYPE_BY_UPLOAD_TYPE: Record<ConfirmationUploadType, FreightDiscriminator> = {
    MATERIAL: "CARGO_MATERIAL",
    EMPTIES: "CARGO_EMPTIES",
}

export const useConfirmationUploadProcessor = (
    uploadType: ConfirmationUploadType
): ConfirmationUploadProcessor => {
    const [getTransportOrdersByNumber] = useLazyGetTransportOrdersByNumberQuery()
    const [postFreightForwarderResponse] = usePostFreightForwarderResponseMutation()
    const assets = useAssetsOfAccount()
    const intl = useIntl()

    return {
        process: async (file, onRowProcessed): Promise<ProcessedUpload> => {
            const rows = await parseConfirmationCsv(file)
            const results = await processConfirmationRows(
                rows,
                assets,
                transportOrderNumber => getTransportOrdersByNumber({
                    transportOrderNumber,
                    freightType: FREIGHT_TYPE_BY_UPLOAD_TYPE[uploadType],
                }).unwrap(),
                confirmation => postFreightForwarderResponse(confirmation).unwrap(),
                uploadType,
                intl,
                onRowProcessed
            )
            return { results }
        },
    }
}
